<template>
  <div
    id="order"
    class="mt-3 container-fluid"
    v-if="orderDetails.id">
    <!-- Page Header -->
    <div class="row">
      <div class="col">
        <div class="d-flex flex-column flex-sm-row align-items-center">
          <h3 class="mb-0 text-center text-sm-left">
            Order Details
          </h3>

          <div
            class="flex-fill text-center"
            :class="{
              'text-danger font-weight-bold': orderDetails.balance_due !== 0,
              'text-success font-weight-bold': orderDetails.balance_due === 0,
            }">
            Balance: {{ orderDetails.balance_due | currency }}
            <span v-if="orderDetails.balance_due > 0">(Payment Due)</span>
            <span v-else-if="orderDetails.balance_due < 0">(Refund Due)</span>
          </div>

          <!-- Enter Payment -->
          <b-dropdown
            class="link-dropdown"
            right
            variant="link"
            text="Enter Non-CC Payment/Refund">
            <b-dropdown-item
              v-for="type in paymentTypeOptions"
              :key="type.type"
              @click="enterPayment(type)">
              {{type.label}}
            </b-dropdown-item>
          </b-dropdown>
          <!-- / Enter Payment -->
        </div>
        <hr>
      </div>
    </div>
    <!-- / Page Header -->

    <!-- Registration Transactions Table -->
    <div class="mb-3 d-flex align-items-center justify-content-between">
      <h4 class="m-0 text-secondary">{{loggedInUser.actingOrg.name}}</h4>
      Total Ordered: {{orderDetails.total_ordered | currency}}
    </div>
    <b-table
      striped
      hover
      small
      stacked="lg"
      show-empty
      empty-text="There are no registrations to show."
      :items="orderDetails.registrationTransactions"
      :fields="registrationTransactionsFields">
      <!-- Registration Transaction Actions -->
      <template
        slot="actions"
        slot-scope="{ item }">
        <b-dropdown
          variant="link"
          boundary="window"
          v-if="isMostRecentOrderForRegistrant(item)">
          <!-- Hamburger -->
          <template slot="button-content">
            <font-awesome-icon icon="bars"></font-awesome-icon>
          </template>
          <!-- / Hamburger -->

          <!-- Options -->
          <b-dropdown-item
            @click="adjust(item)">
            Adjust
          </b-dropdown-item>

          <b-dropdown-item
            @click="cancel(item)"
            :disabled="item.cancelled">
            Cancel
          </b-dropdown-item>

          <b-dropdown-item
            @click="move(item)"
            :disabled="item.cancelled">
            Move
          </b-dropdown-item>
          <!-- / Options -->
        </b-dropdown>
      </template>
      <!-- / Registration Transaction Actions -->

      <!-- Registrant -->
      <template
        slot="full_name"
        slot-scope="{ item }">
        <b-button
          class="p-0 m-0"
          type="button"
          variant="link"
          @click="goToRegistration(item.event.id, item.id)">
          {{item.full_name}}
        </b-button>
      </template>
      <!-- / Registrant -->

      <!-- Event / Pricing Package -->
      <template
        slot="event.name"
        slot-scope="{ item }">
        <div class="d-flex flex-column flex-sm-row align-items-sm-center">
          <!-- Event / Link -->
          <b-button
            class="p-0 m-0 text-left text-sm-center"
            type="button"
            variant="link"
            @click="goToEvent(item.event.id)">
            <div>{{item.event.name}}</div>
          </b-button>
          <!-- / Event / Link -->

          <font-awesome-icon
            class="pl-1 d-none d-sm-block"
            icon="angle-double-right"
            size="sm">
          </font-awesome-icon>

          <!-- Pricing / Link -->
          <b-button
            class="p-0 m-0 pl-sm-1 text-left text-sm-center"
            type="button"
            variant="link"
            v-if="item.reason === 'order' || item.reason === 'other'"
            @click="goToPricing(item.event.id, item.pricing_package_id)"
            :disabled="!hasRole('superadmin')">
            <div>{{item.pricing_package_label}}</div>
          </b-button>
          <!-- / Pricing / Link -->

          <!-- Reason -->
          <div class="pl-1" v-else>{{item.reason | capitalize}}</div>
          <!-- / Reason -->
        </div>

        <!-- Event Start / End -->
        <div v-if="item.reason === 'order' || item.reason === 'other'">
          <div><b>Start:</b> {{item.event.display_start_date_time}}</div>
          <div><b>End:</b> {{item.event.display_end_date_time}}</div>
          <div v-if="item.promo_code"><b>Promo Code:</b> {{item.promo_code.code}}</div>
        </div>
        <!-- Event Start / End -->
      </template>
      <!-- / Event / Pricing Package -->

      <!-- KJK Fees -->
      <template
        slot="KJK Amount"
        slot-scope="{ item }">
        <div v-if="isFirstOrderForRegistrant(item)">{{item['KJK Amount'] | currency}}</div>
      </template>
      <!-- / KJK Fees -->
    </b-table>
    <!-- / Registration Transactions Table -->

    <hr>

    <!-- Payment Transactions Table -->
    <div class="mb-3 d-flex align-items-center justify-content-between">
      <h4 class="m-0 text-secondary">Transaction Details</h4>
      Total Collected: {{orderDetails.total_collected | currency}}
    </div>
    <b-table
      striped
      hover
      small
      stacked="lg"
      show-empty
      empty-text="There are no transactions to show."
      :items="orderDetails.paymentTransactions"
      :fields="paymentTransactionsFields">
      <!-- Method -->
      <template
        slot="payment_type"
        slot-scope="{ item }">
        {{getMethodLabel(item.payment_type)}}<span v-if="item.payment_number"> #{{item.payment_number}}</span>
      </template>
      <!-- / Method -->

      <!-- Registrar -->
      <template
        slot="registrar_full_name"
        slot-scope="{ item }">
        {{item.payment_last_name || orderDetails.last_name}},
        {{item.payment_first_name || orderDetails.first_name}}
      </template>
      <!-- / Registrar -->

      <!-- Registrant -->
      <template
        slot="full_name"
        slot-scope="{ item }">
        <b-button
          class="p-0 m-0"
          type="button"
          variant="link"
          @click="goToRegistration(item.event.id, item.id)">
          {{item.full_name}}
        </b-button>
      </template>
      <!-- / Registrant -->

      <!-- Event -->
      <template
        slot="event.name"
        slot-scope="{ item }">
        <div class="d-flex align-items-center">
          <b-button
            class="p-0 m-0"
            type="button"
            variant="link"
            @click="goToEvent(item.event.id)">
            <div>{{item.event.name}}</div>
          </b-button>
          <font-awesome-icon
            class="pl-1"
            icon="angle-double-right"
            size="sm">
          </font-awesome-icon>
          <b-button
            class="p-0 m-0 pl-1"
            type="button"
            variant="link"
            @click="goToPricing(item.event.id, item.pricing_package_id)"
            :disabled="!hasRole('superadmin')">
            <div>{{item.pricing_package_label}}</div>
          </b-button>
        </div>
        <div><b>Start:</b> {{item.event.display_start_date_time}}</div>
        <div><b>End:</b> {{item.event.display_end_date_time}}</div>
      </template>
      <!-- / Event -->
    </b-table>
    <!-- / Payment Transactions Table -->

    <!-- Adjust / Cancel Modal -->
    <b-modal
      id="adjust-cancel-modal"
      ref="adjustCancelModal"
      v-model="adjustCancelModal"
      :title="adjustCancelModalTitle"
      @hidden="resetAdjustCancel"
      hide-footer>
      <b-form
        id="adjust-cancel-form"
        name="adjustCancelForm">
        <div class="row">
          <div class="col-12 col-sm">
            <!-- Amount Paid -->
            <div>
              <label for="amount-paid">Amount Paid:</label>
              <Number
                id="amount-paid"
                ref="amountPaid"
                name="amountPaid"
                type="currency"
                :value="adjustCancel.amountPaid"
                disabled
              />
            </div>
            <!-- / Amount Paid -->

            <!-- Adjustment -->
            <div class="mt-3">
              <label for="adjustment">Adjustment Amount:</label>
              <Number
                id="adjustment"
                ref="adjustment"
                name="adjustment"
                type="currency"
                step="10"
                min="-100000"
                v-model="adjustCancel.amount"
                :disabled="submitting"
              />
            </div>
            <!-- / Adjustment -->

            <!-- Net Paid -->
            <div class="mt-3">
              <label for="net">Net:</label>
              <Number
                id="net"
                ref="net"
                name="net"
                type="currency"
                :value="net"
                disabled
              />
            </div>
            <!-- / Net Paid -->
          </div>

          <div class="col-12 col-sm mt-3 mt-sm-0">
            <!-- Cost Of Registration -->
            <div>
              <label for="cost">Price:</label>
              <Number
                id="cost"
                ref="cost"
                name="cost"
                type="currency"
                :value="adjustCancel.costOfRegistration"
                disabled
              />
            </div>
            <!-- / Cost Of Registration -->

            <!-- Refund -->
            <div class="mt-3">
              <label class="d-none d-sm-block">&nbsp;</label>
              <b-form-checkbox
                id="adjust-refund"
                class="form-control"
                name="adjustRefund"
                ref="adjustRefund"
                :disabled="!isRefundDue || submitting"
                v-model="adjustCancelRefund">
                Issue Credit Card Refund
              </b-form-checkbox>
            </div>
            <!-- / Refund -->

            <!-- Balance -->
            <div class="mt-3">
              <label
                for="balance-net"
                :class="{
                  'text-danger font-weight-bold': balanceNet !== 0,
                  'text-success font-weight-bold': balanceNet === 0,
                }">
                Balance:
                <span v-if="balanceNet > 0">(Payment Due)</span>
                <span v-else-if="balanceNet < 0">(Refund Due)</span>
              </label>
              <Number
                id="balance-net"
                ref="balanceNet"
                name="balanceNet"
                type="currency"
                :value="balanceNetString"
                disabled
              />
            </div>
            <!-- / Balance -->
          </div>
        </div>

        <!-- Form Buttons -->
        <div class="mt-4 d-flex justify-content-end">
          <b-button
            type="submit"
            class="ml-3"
            variant="success"
            :disabled="submitting"
            @click.prevent="submitAdjustCancel(adjustCancel, adjustCancelAction, adjustCancelRefund)">
            {{adjustCancelAction}}
            <font-awesome-icon icon="check-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="$refs.adjustCancelModal.hide();"
            :disabled="submitting">
            Close
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Adjust / Cancel Modal -->

    <!-- Move Modal -->
    <b-modal
      id="move-modal"
      ref="moveModal"
      v-model="moveModal"
      @hidden="resetMove"
      title="Move"
      hide-footer>
      <b-form
        id="move-form"
        name="moveForm">
        <!-- Season & Event -->
        <div class="row">
          <!-- Season -->
          <div class="col-12 col-sm">
            <label for="season">Season:</label>
            <b-form-select
              id="season"
              ref="season"
              name="season"
              @input="getEvents(season, moveData.originalEventID)"
              v-model="season"
              :disabled="submitting">
              <option :value="undefined">All Seasons</option>
              <option
                v-for="season in seasons"
                :key="season.id"
                :value="season.id">
                {{season.name}}
              </option>
            </b-form-select>
          </div>
          <!-- / Season -->
        </div>

        <div class="row mt-3">
          <!-- Event -->
          <div class="col-12 col-sm">
            <label for="event">New Event:</label>
            <b-form-select
              id="event"
              ref="event"
              name="event"
              :disabled="events.length === 0 || submitting"
              @input="moveData.eventID && getPricingPackages(moveData.eventID)"
              v-model="moveData.eventID">
              <option :value="undefined">
                {{events.length ? 'Select Event' : 'No events for season.'}}
              </option>
              <option
                v-for="e in events"
                :key="e.id"
                :value="e.id">
                {{e.name}}
              </option>
            </b-form-select>
          </div>
          <!-- / Event -->

          <!-- Pricing Package -->
          <div class="col-12 col-sm mt-3 mt-sm-0">
            <label for="price">New Price:</label>
            <b-form-select
              id="price"
              ref="price"
              name="price"
              :disabled="pricing.length <= 1 || submitting"
              v-model="moveData.pricingPackageID">
              <option :value="undefined">
                {{pricing.length || !moveData.eventID ? 'Select Price' : 'No pricing for this event.'}}
              </option>
              <option
                v-for="p in pricing"
                :key="p.id"
                :value="p.id">
                {{p.price_total | currency}} - {{p.label}}
              </option>
            </b-form-select>
          </div>
          <!-- / Pricing Package -->
        </div>
        <!-- / Season & Event -->

        <!-- Amount Paid & Pricing Package -->
        <div
          class="row mt-3">
          <!-- Amount Paid -->
          <div class="col-12 col-sm">
            <label for="amount-paid">Amount Paid:</label>
            <Number
              id="amount-paid"
              ref="amountPaid"
              name="amountPaid"
              type="currency"
              :value="moveData.amountPaid"
              disabled
            />
          </div>
          <!-- / Amount Paid -->

          <!-- Cost Of Registration -->
          <div class="col-12 col-sm mt-3 mt-sm-0">
            <label for="cost-move">Old Price:</label>
            <Number
              id="cost-move"
              ref="costMove"
              name="costMove"
              type="currency"
              :value="moveData.costOfRegistration"
              disabled
            />
          </div>
          <!-- / Cost Of Registration -->
        </div>
        <!-- / Amount Paid & Pricing Package -->

        <!-- Net & Refund -->
        <div
          class="row mt-3">
          <!-- Net -->
          <div class="col-12 col-sm">
            <label for="net">Net:</label>
            <Number
              id="net"
              ref="net"
              name="net"
              type="currency"
              :value="moveNet"
              disabled
            />
          </div>
          <!-- / Net -->

          <!-- Refund -->
          <div class="col-12 col-sm">
            <label class="d-none d-sm-block">&nbsp;</label>
            <b-form-checkbox
              id="move-refund"
              class="mt-3"
              name="moveRefund"
              ref="moveRefund"
              :disabled="!isMoveRefundDue || submitting"
              v-model="moveRefund">
              Issue Credit Card Refund
            </b-form-checkbox>
          </div>
          <!-- / Refund -->
        </div>
        <!-- / Net & Refund -->

        <div class="row mt-3">
          <!-- Balance -->
          <div class="col-6">
            <label
              for="balance-net-move"
              :class="{
                'text-danger font-weight-bold': balanceNetMove !== 0,
                'text-success font-weight-bold': balanceNetMove === 0,
              }">
              Balance:
              <span v-if="balanceNetMove > 0">(Payment Due)</span>
              <span v-else-if="balanceNetMove < 0">(Refund Due)</span>
            </label>
            <Number
              id="balance-net-move"
              ref="balanceNetMove"
              name="balanceNetMove"
              type="currency"
              :value="balanceNetMoveString"
              disabled
            />
          </div>
          <!-- / Balance -->
        </div>

        <!-- Form Buttons -->
        <div class="mt-4 d-flex justify-content-end">
          <b-button
            type="submit"
            class="ml-3"
            variant="success"
            :disabled="!moveData.eventID || !moveData.pricingPackageID || submitting"
            @click.prevent="submitMove(moveData, moveRefund ?  moveNet : false)">
            Move
            <font-awesome-icon icon="check-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="$refs.moveModal.hide();"
            :disabled="submitting">
            Close
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Move Modal -->

    <!-- Payment Modal -->
    <b-modal
      id="payment-modal"
      ref="paymentModal"
      v-model="paymentModalOpen"
      :title="`Enter ${paymentType} ${payment.refund ? 'Refund' : 'Payment'}`"
      @hidden="resetPayment"
      hide-footer>
      <b-form
        id="payment-form"
        name="paymentForm"
        v-on:submit.prevent="submitPaymentOrRefund(payment)">
        <!-- Line Item -->
        <div class="row">
          <!-- Line Item -->
          <div class="col">
            <label for="payment-line-item">
              Apply {{payment.refund ? 'Refund' : 'Payment'}} To:
              <small class="text-danger">required</small>
            </label>
            <b-form-select
              id="payment-line-item"
              ref="paymentLineItem"
              name="paymentLineItem"
              :disabled="submitting"
              @input="defautlAmountIfRefund"
              v-model="payment.registrationID">
              <option selected :value="undefined">Select Line Item</option>
              <option
                v-for="rt in applyToOptions"
                :key="rt.id"
                :value="rt.id">
                {{rt.event.name}} - {{rt.pricing_package_label}} - {{rt.full_name}}
              </option>
            </b-form-select>
          </div>
          <!-- Line Item -->
        </div>
        <!-- / Line Item -->

        <!-- First Name -->
        <div class="row mt-3">
          <!-- First Name -->
          <div class="col">
            <label for="payment-first-name">Payor First Name:</label>
            <b-form-input
              id="payment-first-name"
              ref="paymenFirstName"
              name="paymenFirstName"
              type="text"
              :placeholder="`First Name${nonCash ? ` From ${paymentType}` : '' }`"
              :disabled="submitting"
              v-model="payment.payment_first_name">
            </b-form-input>
          </div>
          <!-- First Name -->
        </div>
        <!-- First Name -->

        <!-- Last Name -->
        <div class="row mt-3">
          <!-- Last Name -->
          <div class="col">
            <label for="payment-last-name">Payor Last Name:</label>
            <b-form-input
              id="payment-last-name"
              ref="paymenLastName"
              name="paymenLastName"
              type="text"
              :placeholder="`Last Name${nonCash ? ` From ${paymentType}` : '' }`"
              :disabled="submitting"
              v-model="payment.payment_last_name">
            </b-form-input>
          </div>
          <!-- Last Name -->
        </div>
        <!-- / Last Name -->

        <!-- Number -->
        <div
          v-if="nonCash"
          class="row mt-3">
          <div class="col">
            <label for="payment-number">{{paymentType}} Number:</label>
            <b-form-input
              id="payment-number"
              ref="paymentNumber"
              name="paymentNumber"
              type="text"
              :placeholder="`Enter ${paymentType} Number`"
              :disabled="submitting"
              v-model="payment.payment_number">
            </b-form-input>
          </div>
        </div>
        <!-- / Number -->

        <!-- Amount -->
        <div class="row mt-3">
          <div class="col">
            <label for="payment-amount">
              Amount:
              <small class="text-danger">required</small>
            </label>
            <Number
              id="payment-amount"
              ref="paymentAmount"
              name="paymentAmount"
              type="currency"
              step="10"
              :placeholder="`Enter ${paymentType} Amount`"
              :disabled="submitting"
              v-model="payment.amount"
            />
          </div>
        </div>
        <!-- / Amount -->

        <!-- Form Buttons -->
        <div class="mt-4 d-flex justify-content-end">
          <div class="mr-auto">
            <b-form-checkbox
              id="enter-refund"
              class="form-control"
              name="enterRefund"
              ref="enterRefund"
              :disabled="submitting"
              @input="defautlAmountIfRefund"
              v-model="payment.refund">
              This is a refund
            </b-form-checkbox>
          </div>

          <b-button
            type="submit"
            class="ml-3"
            variant="success"
            :disabled="submitting">
            Submit
            <font-awesome-icon icon="check-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="paymentModalOpen = false;"
            :disabled="submitting">
            Close
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Payment Modal -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import AppMixin from '../services/app-mixin';
import Formatters from '../services/formatters';
import EventBus from '../services/event-bus';

export default {
  name: 'order',
  mixins: [
    AppMixin,
  ],
  data() {
    return {
      // registration transactions
      paymentTypes: [],
      paymentType: '',
      registrationTransactionsFields: [
        {
          key: 'actions',
          label: '',
          class: 'action-column text-center',
        },
        {
          key: 'display_created_at',
          label: 'Order Placed',
        },
        {
          key: 'full_name',
          label: 'Registrant',
        },
        {
          key: 'event.name',
          label: 'Event',
        },
        {
          key: 'amount',
          class: 'text-lg-right',
          formatter: Formatters.currency,
        },
        {
          key: 'amount_fees',
          label: 'Fee',
          class: 'text-lg-right',
          formatter: Formatters.currency,
        },
        {
          key: 'amount_total',
          label: 'Total',
          class: 'text-lg-right',
          formatter: Formatters.currency,
        },
        {
          key: 'amount_kjk_fees',
          label: 'KJK Amount',
          class: 'text-lg-right',
          formatter: Formatters.currency,
        },
      ],

      // payment transactions
      paymentTransactionsFields: [
        {
          key: 'display_created_at',
          label: 'Transaction Created',
        },
        {
          key: 'amount',
          class: 'text-lg-right',
          formatter: Formatters.currency,
        },
        {
          key: 'payment_type',
          label: 'Method',
        },
        {
          key: 'registrar_full_name',
          label: 'User',
        },
        {
          key: 'full_name',
          label: 'Registrant',
        },
        {
          key: 'event.name',
          label: 'Event',
        },
      ],

      // adjust / cancel modal
      submitting: false,
      adjustCancelModal: false,
      adjustCancelModalTitle: '',
      adjustCancelAction: '',
      adjustCancel: {},
      adjustCancelRefund: false,

      // move modal
      moveModal: false,
      moveData: {
        pricingPackageID: null,
        eventID: null,
        amountPaid: null,
        originalEventID: null,
        registrationID: null,
      },
      season: undefined,
      events: [],
      pricing: [],
      moveRefund: false,

      // registrant
      orderDetails: {},

      // payment
      payment: {},
      paymentModalOpen: false,
      refundTypes: [],
    };
  },
  computed: {
    net() {
      const net = parseFloat(this.adjustCancel.amountPaid) + parseFloat(this.adjustCancel.amount);
      return net.toFixed(2);
    },
    balanceNetString() {
      return this.balanceNet.toFixed(2);
    },
    balanceNet() {
      const net = this.adjustCancelRefund
        ? 0
        : parseFloat(this.adjustCancel.amount);

      return this.orderDetails.balance_due + net;
    },
    balanceNetMoveString() {
      return this.balanceNetMove.toFixed(2);
    },
    balanceNetMove() {
      const net = this.moveRefund
        ? 0
        : parseFloat(this.moveNet);

      const moveAdjust = this.moveData.pricingPackageID
        ? this.moveData.amountPaid - this.moveData.costOfRegistration
        : 0;

      return this.orderDetails.balance_due + net + moveAdjust;
    },
    isRefundDue() {
      return parseFloat(this.adjustCancel.amount) < 0;
    },
    moveNet() {
      const newEventPrice = (_.find(this.pricing, { id: this.moveData.pricingPackageID }) || {}).price_total;
      let net = parseFloat(newEventPrice) - parseFloat(this.moveData.amountPaid);

      if (isNaN(net)) {
        net = 0;
      }

      return net.toFixed(2);
    },
    isMoveRefundDue() {
      return parseFloat(this.moveNet) < 0;
    },
    nonCash() {
      return this.payment.payment_type !== 'cash';
    },
    applyToOptions() {
      const last = [];

      _(this.orderDetails.registrationTransactions)
        .groupBy('id')
        .forEach((transactions) => {
          last.push(_.last(transactions));
        });

      return last;
    },
    paymentTypeOptions() {
      return _.reject(this.paymentTypes, pt => pt.type.includes('credit_card'));
    },
  },
  watch: {
    isMoveRefundDue(refund) {
      if (!refund) {
        this.moveRefund = false;
      }
    },
    isRefundDue(refund) {
      if (!refund) {
        this.adjustCancelRefund = false;
      }
    },
  },
  methods: {
    // payment
    enterPayment(type) {
      this.paymentType = type.label;
      this.payment = { payment_type: type.type };
      this.paymentModalOpen = true;
    },
    defautlAmountIfRefund() {
      if (this.payment.refund
        && !this.payment.amount
        && this.payment.registrationID) {
        const registration = _.find(
          this.applyToOptions,
          { registration_id: this.payment.registrationID },
        );

        this.payment.amount = registration.amount_refundable;
      }
    },
    submitPaymentOrRefund(payment) {
      let promise;

      if (payment.registrationID) {
        const confirm = () => {
          this.showToast(`Adding ${payment.refund ? 'refund' : 'payment'}...`);
          this.submitting = true;

          // if payment is a refund, apply that type instead
          if (payment.refund) {
            payment.refund_type = `${payment.payment_type}_refund`;
            delete payment.payment_type;
            promise = DataSvc.recordRefund(payment.registrationID, payment);
          } else {
            promise = DataSvc.recordPayment(payment.registrationID, payment);
          }

          promise
            .then(() => this.getOrderDetails(this.orderDetails.uuid))
            .then(() => {
              this.paymentModalOpen = false;
              this.showToast(`${payment.refund ? 'Refund' : 'Payment'} added!`);
            })
            .finally(() => this.delay(1000))
            .finally(() => {
              this.submitting = false;
              this.hideToast();
            });
        };

        const registration = _.find(
          this.applyToOptions,
          { registration_id: this.payment.registrationID },
        );

        promise = this.refundAmountCheck(
          payment.refund,
          payment.amount,
          registration,
          true,
        ).then(confirm, _.noop);
      } else {
        promise = this.showWarning(
          `&bull; Apply ${payment.refund ? 'Refund' : 'Payment'} To is required`,
          'The following errors occured',
        );
      }

      return promise;
    },
    getMethodLabel(type) {
      const types = this.paymentTypes.concat(this.refundTypes);
      return (_.find(types, { type }) || {}).label;
    },
    resetPayment() {
      this.payment = {};
      this.paymentType = '';
    },

    // adjust / cancel
    adjust(registrationTransaction) {
      const paymentTransaction = _.find(
        this.orderDetails.paymentTransactions,
        { registration_id: registrationTransaction.registration_id },
      );

      this.adjustCancelModalTitle = 'Adjustment';
      this.adjustCancelAction = 'Adjust';

      this.adjustCancel = {
        amountPaid: paymentTransaction ? paymentTransaction.amount : 0,
        costOfRegistration: registrationTransaction.amount_total,
        amount: 0,
        registration: _.cloneDeep(registrationTransaction),
      };

      this.adjustCancelModal = true;
    },
    cancel(registrationTransaction) {
      const paymentTransaction = _.find(
        this.orderDetails.paymentTransactions,
        { registration_id: registrationTransaction.registration_id },
      );

      this.adjustCancelModalTitle = 'Cancellation';
      this.adjustCancelAction = 'Cancel';

      this.adjustCancel = {
        amountPaid: paymentTransaction ? paymentTransaction.amount : 0,
        costOfRegistration: registrationTransaction.amount_total,
        amount: -registrationTransaction.amount_refundable,
        registration: _.cloneDeep(registrationTransaction),
      };

      this.adjustCancelModal = true;
    },
    submitAdjustCancel(adjustCancel, action, refund) {
      const confirm = () => {
        let refundPromise = Promise.resolve();

        const adjustment = {
          reason: {
            Cancel: 'cancellation',
            Adjust: 'adjustment',
          }[action],
          amount: adjustCancel.amount,
        };

        this.showToast(`Submitting ${adjustment.reason}...`);
        this.submitting = true;

        // if refund to be issued
        if (refund) {
          refundPromise = DataSvc.issueRegistrationRefund(
            adjustCancel.registration.registration_id,
            adjustment,
          );
        }

        return refundPromise
          .then(() => Promise.all([
            action === 'Cancel'
              ? DataSvc.cancelRegistration(adjustCancel.registration.registration_id)
              : Promise.resolve(),
            DataSvc.adjustRegistration(adjustCancel.registration.registration_id, adjustment),
          ]))
          .then(() => this.getOrderDetails(this.orderDetails.uuid))
          .then(() => {
            this.adjustCancelModal = false;
            this.showToast(`${_.startCase(adjustment.reason)} submitted!`);
          })
          .finally(() => this.delay(1000))
          .finally(() => {
            this.submitting = false;
            this.hideToast();
          });
      };

      let promise;

      if (action === 'Cancel') {
        const cancelConfirmPromise = this.showConfirm(
          `This is a <b class='text-danger'>permanent</b> action that
          <b class='text-danger'>cannot be reversed.</b><br><br>
          The only way to reinstate this registration is by:
          <ul>
            <li>Manually creating a new one yourself</li>
            <li>Having the registrant re-register</li>
          </ul>`,
          'Are you sure you want to cancel?',
        );

        promise = cancelConfirmPromise
          .then(() => this.refundAmountCheck(
            refund,
            adjustCancel.amount,
            adjustCancel.registration,
          ), () => Promise.reject())
          .then(confirm, _.noop);
      } else {
        promise = this.refundAmountCheck(
          refund,
          adjustCancel.amount,
          adjustCancel.registration,
        ).then(confirm, _.noop);
      }

      return promise;
    },
    resetAdjustCancel() {
      this.adjustCancel = {};
      this.adjustCancelRefund = false;
    },

    // move
    move(registrationTransaction) {
      const paymentTransaction = _.find(
        this.orderDetails.paymentTransactions,
        { registration_id: registrationTransaction.registration_id },
      );

      this.moveData = {
        amountPaid: paymentTransaction ? paymentTransaction.amount : 0,
        costOfRegistration: registrationTransaction.amount_total,
        registrationID: registrationTransaction.registration_id,
        originalEventID: registrationTransaction.event.id,
      };

      this.season = this.loggedInUser.actingSeason.id;

      return this.getEvents(this.season, registrationTransaction.event.id)
        .then(() => {
          this.moveModal = true;
        });
    },
    resetMove() {
      this.moveData = {};
      this.season = undefined;
      this.events = [];
      this.pricing = [];
      this.moveRefund = false;
    },
    getEvents(seasonID, id) {
      return DataSvc.getEvents(this.loggedInUser.actingOrg.id, seasonID)
        .then((events) => {
          this.events = _(events)
            .reject({ id })
            .orderBy('name')
            .value();

          this.moveData.eventID = undefined;
          this.moveData.pricingPackageID = undefined;
        });
    },
    getPricingPackages(eventID) {
      return DataSvc.getEventPricing(eventID)
        .then((pricing) => {
          this.pricing = pricing;
          this.moveData.pricingPackageID = undefined;

          if (pricing.length === 1) {
            this.moveData.pricingPackageID = pricing[0].id;
          } else if (pricing.length === 0) {
            this.showConfirm(
              `You must set up pricing for an event before
              moving registrants to its roster. Would you like
              to set up pricing now?`,
              'No Pricing For Event',
            ).then(() => {
              this.goToPricing(eventID, 'new');
            }, () => {});
          }
        });
    },
    submitMove(moveData, refundAmount) {
      let refundPromise = Promise.resolve();

      if (refundAmount !== false) {
        refundPromise = DataSvc.issueRegistrationRefund(moveData.registrationID, { amount: refundAmount });
      }

      this.showToast('Moving registration...');
      this.submitting = true;

      // refund first
      refundPromise
        .then(() => DataSvc.moveRegistration(moveData.registrationID, moveData))
        .then(() => this.getOrderDetails(this.orderDetails.uuid))
        .then(() => {
          this.moveModal = false;
          this.showToast('Registration moved!');
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });
    },

    // other
    getOrderDetails(registrationSessionUUID) {
      return DataSvc.getOrderDetails(registrationSessionUUID)
        .then((orderDetails) => {
          this.orderDetails = orderDetails;
        });
    },
    isMostRecentOrderForRegistrant(transaction) {
      const recent = _(this.orderDetails.registrationTransactions)
        .filter(t => (t.reason === 'order' || t.reason === 'other') && t.id === transaction.id)
        .last();

      return transaction.transaction_id === recent.transaction_id;
    },
    isFirstOrderForRegistrant(transaction) {
      const first = _(this.orderDetails.registrationTransactions)
        .filter({ reason: 'order', id: transaction.id })
        .first();

      return transaction.transaction_id === first.transaction_id;
    },
    refundAmountCheck(refund, amount, registration, isNonCC) {
      return this.$nextTick()
        .then(() => {
          let orgFeeConfirmPromise = Promise.resolve();
          const amt = Math.abs(amount);
          const refundDecimal = parseFloat(registration.amount_refundable).toFixed(2);
          const amountDecimal = parseFloat(amt).toFixed(2);

          if (refund
              && amt > registration.amount_refundable
              && amt <= registration.total_collected) {
            orgFeeConfirmPromise = this.showConfirm(
              `Proceeding with this refund will refund part or all of
              the Organization Fees collected for ${registration.full_name}.<br><br>
              Issuing a refund of $${refundDecimal} or less will
              prevent this from happening.`,
              `Are you sure you want to refund $${amountDecimal}?`,
            );
          } else if (refund
              && isNonCC
              && amt > registration.total_collected) {
            orgFeeConfirmPromise = this.showConfirm(
              `Proceeding with this refund will refund more
              than the total amount collected for ${registration.full_name}.`,
              `Are you sure you want to refund $${amountDecimal}?`,
            );
          }

          return orgFeeConfirmPromise;
        });
    },
  },
  created() {
    this.showGlobalLoader('Loading order...');

    const initOrder = () => {
      const { registrationSessionUUID } = this.$route.params;

      // get registration session
      const orderPromise = this.getOrderDetails(registrationSessionUUID)
        .then(() => {
          const firstEvent = _.first(this.orderDetails.registrationTransactions).event;

          if (this.loggedInUser.actingOrg.id !== firstEvent.org_id) {
            this.findAndSwitchActingOrg(firstEvent.org_id);
          }
        })
        .finally(() => {
          this.$nextTick(() => {
            // watch for org change and route to events page
            EventBus.$on('acting-org-changed', this.goToEvents);
          });
        });

      // get payment types
      const paymentTypesPromise = DataSvc.getPaymentTypes()
        .then((paymentTypes) => {
          this.paymentTypes = _(paymentTypes)
            .map(pt => ({
              type: pt,
              label: _.startCase(pt.replace(/_/g, ' ')),
            }))
            .orderBy('label')
            .value();
        });

      // get refund types
      const refundTypesPromise = DataSvc.getRefundTypes()
        .then((refundTypes) => {
          this.refundTypes = _(refundTypes)
            .map(rt => ({
              type: rt,
              label: _.startCase(rt.replace(/_/g, ' ')),
            }))
            .orderBy('label')
            .value();
        });

      return Promise.all([orderPromise, paymentTypesPromise, refundTypesPromise]);
    };

    this.initPromise.then(initOrder).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>

<style lang="scss">
#order {
  .b-table {
    tbody {
      td {
        vertical-align: middle;
      }
    }
  }

  .relative-labels {
    label {
      position: relative;
    }

    .group-math {
      position: absolute;
      height: calc(100% + 15px);
      width: calc(100% - 15px);
      left: 7.5px;
      top: -7.5px;
      background: #f2f2f2;
      z-index: 0;
    }
  }
}
</style>
